import type { QuickBooksFpaStatementRender, QuickBooksFpaStatementRow } from '../../../data/ServerApiClient'
import { buildMappedDirectCashStatement, isDirectCashForecast } from './forecastDirectCash'
import {
  addVectors,
  cloneRowWithValues,
  emptyValues,
  hasStatementValue,
  normalizeForecastRowLabel,
} from './forecastDriverEngine'

const forecastStatementMergeKeys = (row: QuickBooksFpaStatementRow): string[] => {
  const keys: string[] = []
  const statementType = normalizeForecastRowLabel(row.statement_type)
  const rowType = normalizeForecastRowLabel(row.row_type)
  const label = normalizeForecastRowLabel(row.label)
  const accountId = normalizeForecastRowLabel(row.account_id)
  const accountNumber = normalizeForecastRowLabel(row.account_number)
  if (row.node_id) keys.push(`node:${row.node_id}`)
  if (accountId) keys.push(`account-id:${statementType}:${accountId}`)
  if (accountNumber && label) keys.push(`account-number-label:${statementType}:${accountNumber}:${label}`)
  if (accountNumber) keys.push(`account-number:${statementType}:${accountNumber}`)
  if (label) keys.push(`label-row:${statementType}:${rowType}:${label}`)
  if (label) keys.push(`label:${statementType}:${label}`)
  return [...new Set(keys)]
}

const indexForecastRowsForMerge = (rows: QuickBooksFpaStatementRow[]) => {
  const byKey = new Map<string, QuickBooksFpaStatementRow[]>()
  rows.forEach((row) => {
    forecastStatementMergeKeys(row).forEach((key) => {
      const bucket = byKey.get(key) || []
      bucket.push(row)
      byKey.set(key, bucket)
    })
  })
  return byKey
}

const rowForecastValues = (row: QuickBooksFpaStatementRow, actualCount: number): Array<number | null> =>
  row.values.slice(actualCount).map((value) => {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  })

const forecastSegmentHasValue = (row: QuickBooksFpaStatementRow, actualCount: number): boolean =>
  hasStatementValue(rowForecastValues(row, actualCount))

const setForecastSegment = (
  row: QuickBooksFpaStatementRow,
  actualCount: number,
  values: Array<number | null>,
) => {
  values.forEach((value, index) => {
    row.values[actualCount + index] = value
  })
}

const copyBalanceSheetForecastValuesIfBlank = (
  rows: QuickBooksFpaStatementRow[],
  actualCount: number,
  fromLabels: string[],
  toLabels: string[],
) => {
  const normalizedFrom = new Set(fromLabels.map(normalizeForecastRowLabel))
  const source = rows.find((row) => normalizedFrom.has(normalizeForecastRowLabel(row.label)) && forecastSegmentHasValue(row, actualCount))
  if (!source) return
  const values = rowForecastValues(source, actualCount)
  const normalizedTo = new Set(toLabels.map(normalizeForecastRowLabel))
  rows
    .filter((row) => normalizedTo.has(normalizeForecastRowLabel(row.label)) && !forecastSegmentHasValue(row, actualCount))
    .forEach((row) => setForecastSegment(row, actualCount, values))
}

const rollUpMergedBalanceSheetForecastRows = (
  rows: QuickBooksFpaStatementRow[],
  actualCount: number,
): QuickBooksFpaStatementRow[] => {
  const cloned = rows.map((row) => cloneRowWithValues(row, row.values))
  const byNodeId = new Map(cloned.map((row) => [row.node_id, row]))
  const depthCache = new Map<string, number>()
  const rowDepth = (row: QuickBooksFpaStatementRow): number => {
    const cached = depthCache.get(row.node_id)
    if (cached != null) return cached
    const parent = row.parent_node_id ? byNodeId.get(row.parent_node_id) : null
    const depth = parent ? rowDepth(parent) + 1 : Number(row.level || 0)
    depthCache.set(row.node_id, depth)
    return depth
  }
  const directForecastNodeIds = new Set(
    cloned
      .filter((row) => forecastSegmentHasValue(row, actualCount))
      .map((row) => row.node_id),
  )
  cloned
    .filter((row) => row.parent_node_id)
    .sort((left, right) => rowDepth(right) - rowDepth(left))
    .forEach((row) => {
      if (!forecastSegmentHasValue(row, actualCount)) return
      const parent = row.parent_node_id ? byNodeId.get(row.parent_node_id) : null
      if (!parent || directForecastNodeIds.has(parent.node_id)) return
      const summed = addVectors(rowForecastValues(parent, actualCount), rowForecastValues(row, actualCount))
      setForecastSegment(parent, actualCount, summed)
    })

  copyBalanceSheetForecastValuesIfBlank(
    cloned,
    actualCount,
    ['Bank Accounts', 'Total Bank Accounts', 'Cash', 'Total Cash', 'Cash and Cash Equivalents'],
    ['Total Bank Accounts'],
  )
  copyBalanceSheetForecastValuesIfBlank(cloned, actualCount, ['Current Assets'], ['Total Current Assets'])
  copyBalanceSheetForecastValuesIfBlank(cloned, actualCount, ['Fixed Assets'], ['Total Fixed Assets'])
  copyBalanceSheetForecastValuesIfBlank(cloned, actualCount, ['Other Assets'], ['Total Other Assets'])
  copyBalanceSheetForecastValuesIfBlank(cloned, actualCount, ['ASSETS', 'Assets'], ['TOTAL ASSETS', 'Total Assets'])
  copyBalanceSheetForecastValuesIfBlank(cloned, actualCount, ['Liabilities'], ['Total Liabilities'])
  copyBalanceSheetForecastValuesIfBlank(cloned, actualCount, ['Equity'], ['Total Equity'])
  copyBalanceSheetForecastValuesIfBlank(
    cloned,
    actualCount,
    ['LIABILITIES AND EQUITY', 'Liabilities and Equity', 'Total Liabilities and Equity'],
    ['TOTAL LIABILITIES AND EQUITY', 'Total Liabilities and Equity'],
  )
  return cloned
}

export const mergeActualAndForecastStatements = (
  actual: QuickBooksFpaStatementRender | null,
  forecast: QuickBooksFpaStatementRender | null,
  historyMonths: number,
  futureMonths: number,
  actualBasis: QuickBooksFpaStatementRender | null = null,
  actualBalanceSheet: QuickBooksFpaStatementRender | null = null,
  actualBalanceSheetSource: QuickBooksFpaStatementRender | null = null,
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null => {
  if (!forecast) return null
  if (historyMonths > 0 && isDirectCashForecast(forecast)) {
    return buildMappedDirectCashStatement(actual, actualBasis, actualBalanceSheet, forecast, historyMonths, futureMonths, actualBalanceSheetSource)
  }
  if (!actual || historyMonths <= 0) {
    const forecastPeriods = forecast.periods.slice(0, Math.max(1, futureMonths))
    return {
      ...forecast,
      periods: forecastPeriods,
      rows: forecast.rows.map((row) => cloneRowWithValues(row, row.values.slice(0, forecastPeriods.length))),
      period_kinds: forecastPeriods.map(() => 'forecast'),
    }
  }
  const actualPeriods = actual.periods.slice(-Math.max(0, historyMonths))
  const forecastPeriods = forecast.periods.slice(0, Math.max(1, futureMonths))
  const actualCount = actualPeriods.length
  const forecastCount = forecastPeriods.length
  const forecastRowsByKey = indexForecastRowsForMerge(forecast.rows)
  const usedForecastRows = new Set<QuickBooksFpaStatementRow>()
  const takeForecastRow = (actualRow: QuickBooksFpaStatementRow): QuickBooksFpaStatementRow | null => {
    for (const key of forecastStatementMergeKeys(actualRow)) {
      const candidate = (forecastRowsByKey.get(key) || []).find((row) => !usedForecastRows.has(row))
      if (candidate) {
        usedForecastRows.add(candidate)
        return candidate
      }
    }
    return null
  }
  const rows: QuickBooksFpaStatementRow[] = []
  for (const row of actual.rows) {
    const forecastRow = takeForecastRow(row)
    const values = [
      ...row.values.slice(-actualCount),
      ...(forecastRow ? forecastRow.values.slice(0, forecastCount) : emptyValues(forecastCount)),
    ]
    rows.push(cloneRowWithValues(row, values))
  }
  for (const row of forecast.rows) {
    if (usedForecastRows.has(row)) continue
    rows.push(cloneRowWithValues(row, [...emptyValues(actualCount), ...row.values.slice(0, forecastCount)]))
  }
  const mergedRows = forecast.statement_type === 'balance_sheet'
    ? rollUpMergedBalanceSheetForecastRows(rows, actualCount)
    : rows
  return {
    ...forecast,
    periods: [...actualPeriods, ...forecastPeriods],
    rows: mergedRows,
    period_kinds: [...actualPeriods.map(() => 'actual'), ...forecastPeriods.map(() => 'forecast')],
  }
}

export const sliceStatementDisplayWindow = (
  statement: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null,
  historyMonths: number,
  futureMonths: number,
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null => {
  if (!statement) return statement
  const periodKinds = statement.period_kinds || statement.periods.map(() => 'forecast')
  const actualHistoryCount = Math.max(0, historyMonths)
  const actualIndexes = actualHistoryCount > 0
    ? periodKinds
      .map((kind, index) => kind === 'actual' ? index : -1)
      .filter((index) => index >= 0)
      .slice(-actualHistoryCount)
    : []
  const forecastIndexes = periodKinds
    .map((kind, index) => kind === 'forecast' ? index : -1)
    .filter((index) => index >= 0)
    .slice(0, Math.max(1, futureMonths))
  const indexes = [...actualIndexes, ...forecastIndexes]
  if (!indexes.length || indexes.length === statement.periods.length) return statement
  return {
    ...statement,
    periods: indexes.map((index) => statement.periods[index]),
    rows: statement.rows.map((row) => cloneRowWithValues(row, indexes.map((index) => row.values[index] ?? null))),
    period_kinds: indexes.map((index) => periodKinds[index] || 'forecast'),
  }
}

export const withDirectCashRollForward = (
  statement: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null,
  beginningCash: number | null,
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null => {
  if (!statement) return statement
  if (!isDirectCashForecast(statement) || beginningCash == null || !Number.isFinite(beginningCash)) return statement
  const netRow = statement.rows.find((row) => row.node_id === 'fg-forecast-cash-net-change')
    || statement.rows.find((row) => /^net cash change$/i.test(row.label || ''))
  if (!netRow) return statement
  const existingBeginningRow = statement.rows.find((row) => row.node_id === 'fg-forecast-cash-beginning-cash')
    || statement.rows.find((row) => /^beginning cash$/i.test(row.label || ''))
  const existingEndingRow = statement.rows.find((row) => row.node_id === 'fg-forecast-cash-ending-cash')
    || statement.rows.find((row) => /^ending cash$/i.test(row.label || ''))
  const periodKinds = statement.period_kinds || statement.periods.map(() => 'forecast')
  const beginningValues = emptyValues(statement.periods.length)
  const endingValues = emptyValues(statement.periods.length)
  let rollingCash = beginningCash
  for (let index = 0; index < statement.periods.length; index += 1) {
    if (periodKinds[index] === 'actual') {
      const actualBeginning = Number(existingBeginningRow?.values?.[index])
      const actualEnding = Number(existingEndingRow?.values?.[index])
      beginningValues[index] = Number.isFinite(actualBeginning) ? actualBeginning : null
      endingValues[index] = Number.isFinite(actualEnding) ? actualEnding : null
      if (Number.isFinite(actualEnding)) rollingCash = actualEnding
      continue
    }
    beginningValues[index] = rollingCash
    rollingCash += Number(netRow.values?.[index] || 0)
    endingValues[index] = rollingCash
  }
  const withoutExistingCashRows = statement.rows.filter((row) => (
    row.node_id !== 'fg-forecast-cash-beginning-cash'
    && row.node_id !== 'fg-forecast-cash-ending-cash'
    && !/^beginning cash$/i.test(row.label || '')
    && !/^ending cash$/i.test(row.label || '')
  ))
  const cashRows: QuickBooksFpaStatementRow[] = [
    {
      node_id: 'fg-forecast-cash-beginning-cash',
      parent_node_id: null,
      row_type: 'formula',
      statement_type: 'cash_flow',
      label: 'Beginning cash',
      level: 0,
      account_id: null,
      account_number: null,
      cash_flow_section: 'direct_cash',
      qbo_account_type: null,
      qbo_account_sub_type: null,
      qbo_classification: null,
      values: beginningValues,
      is_total: true,
      is_formula: true,
      normal_balance: null,
    },
    {
      node_id: 'fg-forecast-cash-ending-cash',
      parent_node_id: null,
      row_type: 'formula',
      statement_type: 'cash_flow',
      label: 'Ending cash',
      level: 0,
      account_id: null,
      account_number: null,
      cash_flow_section: 'direct_cash',
      qbo_account_type: null,
      qbo_account_sub_type: null,
      qbo_classification: null,
      values: endingValues,
      is_total: true,
      is_formula: true,
      normal_balance: null,
    },
  ]
  return {
    ...statement,
    rows: [...withoutExistingCashRows, ...cashRows],
    warnings: [
      ...(statement.warnings || []),
      'Forecast cash balance is rolled forward from the selected reconciled cash balance.',
    ],
  }
}
